import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Typography,
} from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiClient } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import type {
  RecurringInvoiceTemplate,
  RecurringLineItemTemplate,
} from "../../types/billing";
import { recurringTotals, upcomingDates } from "./recurringSchedule";
const money = (n: unknown) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number(n || 0),
  );
export function recurringError(error: any): string {
  const data = error?.responseData || error?.response?.data;
  return data
    ? typeof data === "string"
      ? data
      : Object.entries(data)
          .map(
            ([field, value]) =>
              `${field.replaceAll("_", " ")}: ${Array.isArray(value) ? value.join(", ") : String(value)}`,
          )
          .join(" · ")
    : error?.message || "Unable to save. Please retry.";
}
export function RecurringSummary({
  form,
  generated = 0,
}: {
  form: any;
  generated?: number;
}) {
  const totals = recurringTotals(
    Number(form.fixed_amount),
    form.line_items_template || [],
  );
  const remaining = form.max_occurrences
    ? Math.max(0, Number(form.max_occurrences) - generated)
    : undefined;
  const dates = upcomingDates(
    form.next_invoice_date,
    form.frequency,
    Number(form.frequency_interval),
    form.end_date,
    remaining,
  );
  return (
    <Card variant="soft" color="primary">
      <Typography level="title-lg">Review the billing schedule</Typography>
      <Typography>
        {form.frequency === "custom" ? "Monthly" : form.frequency} · interval{" "}
        {form.frequency_interval} ·{" "}
        {remaining === undefined
          ? "No invoice limit"
          : `${remaining} invoices remaining`}
      </Typography>
      <Typography>
        Next scheduled dates:{" "}
        {dates.join(" · ") || "No dates in the selected range"}
      </Typography>
      <Stack direction="row" sx={{ gap: 3, flexWrap: "wrap" }}>
        <Typography>Subtotal {money(totals.subtotal)}</Typography>
        <Typography>Tax {money(totals.tax)}</Typography>
        <Typography fontWeight="lg">
          Invoice total {money(totals.total)}
        </Typography>
      </Stack>
      {form.line_items_template?.length > 0 &&
        Math.abs(totals.subtotal - Number(form.fixed_amount)) > 0.005 && (
          <Alert color="warning">
            Line item subtotals must match the fixed subtotal before saving.
          </Alert>
        )}
      <Typography level="body-sm">
        {form.is_auto_approve
          ? "New occurrences are automatically approved."
          : "Each occurrence requires approval."}{" "}
        {form.is_auto_send
          ? "Generated invoices will be emailed."
          : "Generated invoices remain drafts for review."}{" "}
        New templates start as drafts; activate after review.
      </Typography>
    </Card>
  );
}
function display(value: any): string {
  if (value == null || value === "") return "None";
  if (Array.isArray(value))
    return value
      .map((v) =>
        typeof v === "object"
          ? `${v.description || "Item"}: ${v.quantity} × ${money(v.unit_price)}`
          : String(v),
      )
      .join("; ");
  return typeof value === "object"
    ? Object.entries(value)
        .map(([k, v]) => `${k.replaceAll("_", " ")}: ${display(v)}`)
        .join(" · ")
    : String(value);
}
export function RecurringWorkspace({ templateId }: { templateId: number }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [page, setPage] = useState(1),
    [schedulePage, setSchedulePage] = useState(1);
  const [until, setUntil] = useState(""),
    [reason, setReason] = useState("");
  const [effective, setEffective] = useState(""),
    [price, setPrice] = useState("");
  const [priceItems, setPriceItems] = useState<RecurringLineItemTemplate[]>([]);
  const query = useQuery({
    queryKey: [
      "recurring-control",
      user?.organization,
      user?.id,
      templateId,
      page,
      schedulePage,
    ],
    queryFn: async () =>
      (
        await apiClient.get<any>(
          `/api/recurring-templates/${templateId}/control/?page=${page}&schedule_page=${schedulePage}`,
        )
      ).data,
  });
  const template: RecurringInvoiceTemplate | undefined = query.data?.template;
  useEffect(() => {
    setPage(1);
    setSchedulePage(1);
    setReason("");
    setEffective("");
    setPrice("");
    setPriceItems([]);
  }, [templateId]);
  const mutation = useMutation({
    mutationFn: async (data: any) =>
      (
        await apiClient.post(
          `/api/recurring-templates/${templateId}/control/`,
          data,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recurring-control"] });
      qc.invalidateQueries({ queryKey: ["recurring-templates"] });
      setReason("");
    },
  });
  const act = (command: string, data: any = {}) =>
    mutation.mutate({ command, ...data });
  if (query.isLoading)
    return <Typography>Loading recurring controls…</Typography>;
  if (query.isError)
    return (
      <Alert
        color="danger"
        endDecorator={<Button onClick={() => query.refetch()}>Retry</Button>}
      >
        Unable to load the recurring schedule.
      </Alert>
    );
  if (!template) return null;
  const planTotal = priceItems.length
    ? recurringTotals(0, priceItems).subtotal
    : Number(price);
  return (
    <Stack spacing={2} sx={{ mt: 3 }}>
      <Card>
        <Typography level="title-lg">Manage future billing</Typography>
        <Typography>
          Status: {template.status}
          {template.pause_until ? ` · Resumes ${template.pause_until}` : ""}
        </Typography>
        <Typography level="body-sm">
          Pausing retains occurrences. Missed dates require approval on resume.
          Skipping a date does not consume the invoice limit.
        </Typography>
        <Stack
          direction="row"
          sx={{ gap: 1, flexWrap: "wrap", alignItems: "end" }}
        >
          {template.status === "active" && (
            <>
              <FormControl>
                <FormLabel>Resume on (optional)</FormLabel>
                <Input
                  type="date"
                  value={until}
                  onChange={(e) => setUntil(e.target.value)}
                />
              </FormControl>
              <Button
                loading={mutation.isPending}
                onClick={() => act("pause", { until: until || null })}
              >
                Pause billing
              </Button>
            </>
          )}
          {template.status === "paused" && (
            <Button loading={mutation.isPending} onClick={() => act("resume")}>
              Resume billing
            </Button>
          )}
        </Stack>
        {template.source_job && (
          <Button
            component={Link}
            to={`/operations/jobs/${template.source_job}`}
            variant="plain"
          >
            Open source job
          </Button>
        )}
        {template.agreement_reference && (
          <Typography>Agreement: {template.agreement_reference}</Typography>
        )}
      </Card>
      <Card>
        <Typography level="title-lg">Schedule a pricing change</Typography>
        {template.price_change?.effective_date && (
          <Alert color="neutral">
            Scheduled subtotal {money(template.price_change.amount)} from{" "}
            {template.price_change.effective_date}. Saving replaces this pending
            plan.
          </Alert>
        )}
        <Typography level="body-sm">
          Choose a future date. Affected upcoming approvals will be cleared for
          review. Existing invoices retain their original amounts.
        </Typography>
        <Stack
          direction="row"
          sx={{ gap: 1, flexWrap: "wrap", alignItems: "end" }}
        >
          <FormControl>
            <FormLabel>Effective date</FormLabel>
            <Input
              type="date"
              value={effective}
              onChange={(e) => setEffective(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>New subtotal</FormLabel>
            <Input
              type="number"
              value={priceItems.length ? planTotal : price}
              disabled={!!priceItems.length}
              onChange={(e) => setPrice(e.target.value)}
              slotProps={{ input: { min: 0.01, step: 0.01 } }}
            />
          </FormControl>
          <Button
            variant="outlined"
            onClick={() =>
              setPriceItems(
                (template.line_items_template?.length
                  ? template.line_items_template
                  : [
                      {
                        description: template.description || template.name,
                        quantity: 1,
                        unit_price: Number(template.fixed_amount),
                        tax_rate: 0,
                      },
                    ]
                ).map((i) => ({ ...i })),
              )
            }
          >
            Edit itemized pricing
          </Button>
        </Stack>
        {priceItems.map((item, index) => (
          <Stack
            key={index}
            direction={{ xs: "column", sm: "row" }}
            sx={{ gap: 1, alignItems: "center" }}
          >
            <Typography sx={{ flex: 1 }}>
              {item.description} · Qty {item.quantity} · Tax{" "}
              {item.tax_rate || 0}%
            </Typography>
            <Input
              aria-label={`New price for ${item.description}`}
              type="number"
              value={item.unit_price}
              onChange={(e) =>
                setPriceItems((items) =>
                  items.map((i, n) =>
                    n === index
                      ? { ...i, unit_price: Number(e.target.value) }
                      : i,
                  ),
                )
              }
              slotProps={{ input: { min: 0, step: 0.01 } }}
            />
          </Stack>
        ))}
        <Typography>
          Current subtotal {money(template.fixed_amount)} → Planned subtotal{" "}
          {money(planTotal)} · Planned tax{" "}
          {money(recurringTotals(planTotal, priceItems).tax)}
        </Typography>
        {!priceItems.length && (
          <Typography level="body-sm">
            A subtotal-only plan uses one untaxed service line. Choose itemized
            pricing to retain tax and item details.
          </Typography>
        )}
        <Button
          disabled={!effective || !(planTotal > 0)}
          loading={mutation.isPending}
          onClick={() =>
            act("price", {
              effective_date: effective,
              amount: String(planTotal.toFixed(2)),
              line_items: priceItems,
            })
          }
        >
          Save pricing plan and request reapproval
        </Button>
      </Card>
      {mutation.isError && (
        <Alert color="danger">{recurringError(mutation.error)}</Alert>
      )}
      <Card>
        <Typography level="title-lg">Upcoming occurrences</Typography>
        <FormControl>
          <FormLabel>Reason when skipping an occurrence</FormLabel>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="For example, service suspended this month"
          />
        </FormControl>
        {query.data.upcoming.map((row: any) => (
          <Box
            key={row.id}
            sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Typography fontWeight="lg">
              {row.scheduled_for} · {money(row.amount)}
            </Typography>
            <Typography level="body-sm">
              Subtotal {money(row.subtotal)} + tax {money(row.tax)} ·{" "}
              {row.requires_approval && !row.is_approved
                ? "Awaiting approval"
                : "Approved"}
            </Typography>
            <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", mt: 1 }}>
              {row.requires_approval && !row.is_approved && (
                <Button
                  size="sm"
                  disabled={mutation.isPending}
                  onClick={() => act("approve", { schedule_id: row.id })}
                >
                  Approve {money(row.amount)}
                </Button>
              )}
              <Button
                size="sm"
                variant="outlined"
                disabled={
                  mutation.isPending ||
                  template.status !== "active" ||
                  (row.requires_approval && !row.is_approved) ||
                  row.scheduled_for > new Date().toLocaleDateString("en-CA")
                }
                onClick={() => act("retry", { schedule_id: row.id })}
              >
                Generate / retry
              </Button>
              <Button
                size="sm"
                variant="plain"
                color="neutral"
                disabled={mutation.isPending || !reason.trim()}
                onClick={() => act("skip", { schedule_id: row.id, reason })}
              >
                Skip occurrence
              </Button>
            </Stack>
          </Box>
        ))}
        {!query.data.upcoming.length && (
          <Typography>No unprocessed occurrences.</Typography>
        )}
        <Stack direction="row" spacing={1}>
          <Button
            disabled={schedulePage === 1}
            onClick={() => setSchedulePage((p) => p - 1)}
          >
            Previous dates
          </Button>
          <Typography>Page {schedulePage}</Typography>
          <Button
            disabled={schedulePage * 10 >= query.data.upcoming_count}
            onClick={() => setSchedulePage((p) => p + 1)}
          >
            Next dates
          </Button>
        </Stack>
      </Card>
      <Card>
        <Typography level="title-lg">Invoices and activity</Typography>
        {query.data.history.map((row: any) => (
          <Box key={row.id}>
            <Typography>
              {row.scheduled_date} · {row.status} · Subtotal{" "}
              {money(row.calculated_amount)}
            </Typography>
            {row.invoice_id && (
              <Button
                component={Link}
                to={`/operations/billing/invoices/${row.invoice_id}`}
                variant="plain"
              >
                Open invoice
              </Button>
            )}
            {row.error_message && (
              <Alert color="danger">{row.error_message}</Alert>
            )}
          </Box>
        ))}
        <Divider />
        {query.data.activities.map((row: any) => (
          <Box key={row.id}>
            <Typography fontWeight="lg">{row.description}</Typography>
            <Typography level="body-sm">
              {row.actor} · {new Date(row.at).toLocaleString()}
            </Typography>
            {row.details.changes &&
              Object.entries(row.details.changes).map(([field, v]: any) => (
                <Typography key={field} level="body-sm">
                  {field.replaceAll("_", " ")}:{" "}
                  {v?.before !== undefined
                    ? `${display(v.before)} → ${display(v.after)}`
                    : display(v)}
                </Typography>
              ))}
            {row.details.reason && (
              <Typography>{row.details.reason}</Typography>
            )}
            {row.details.after && (
              <Typography level="body-sm">
                {display(row.details.before)} → {display(row.details.after)}
              </Typography>
            )}
            {row.error && <Typography color="danger">{row.error}</Typography>}
          </Box>
        ))}
        <Stack direction="row" spacing={1}>
          <Button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Newer
          </Button>
          <Typography>Page {page}</Typography>
          <Button
            disabled={
              page * 20 >=
              Math.max(query.data.history_count, query.data.activity_count)
            }
            onClick={() => setPage((p) => p + 1)}
          >
            Older
          </Button>
        </Stack>
      </Card>
    </Stack>
  );
}
